import { useEffect, useRef, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import { telemetryService } from '../api/telemetryService';

export function useComponentTracking(componentName: string, properties?: Record<string, any>, trackRenderTime: boolean = false) {
  const mountTime = useRef<number>(Date.now());
  const renderCount = useRef<number>(0);

  // Optional render performance tracking
  useEffect(() => {
    if (trackRenderTime) {
      renderCount.current++;
      const renderTime = Date.now() - mountTime.current;

      // Skip initial mount; only log re-renders
      if (renderCount.current > 1) {
        telemetryService.trackComponentPerformance(componentName, renderTime, {
          renderNumber: renderCount.current,
          ...properties
        });
      }
    }
  });
}

export function useActionTracking() {
  const location = useLocation();

  const trackClick = useCallback(
    (elementName: string, elementType?: string, properties?: Record<string, any>) => {
      telemetryService.trackUserAction('Click', elementType || 'button', elementName, {
        path: location.pathname,
        ...properties
      });
    },
    [location.pathname]
  );

  const trackFormSubmit = useCallback(
    (formName: string, properties?: Record<string, any>) => {
      telemetryService.trackFormInteraction(formName, 'submitted', {
        path: location.pathname,
        ...properties
      });
    },
    [location.pathname]
  );

  const trackFormAbandoned = useCallback(
    (formName: string, properties?: Record<string, any>) => {
      telemetryService.trackFormInteraction(formName, 'abandoned', {
        path: location.pathname,
        ...properties
      });
    },
    [location.pathname]
  );

  const trackFormValidationFailed = useCallback(
    (formName: string, errors: string[], properties?: Record<string, any>) => {
      telemetryService.trackFormInteraction(formName, 'validation_failed', {
        path: location.pathname,
        errorCount: errors.length,
        errors: errors.join(', '),
        ...properties
      });
    },
    [location.pathname]
  );

  const trackCustomAction = useCallback(
    (actionName: string, properties?: Record<string, any>) => {
      telemetryService.trackEvent(actionName, {
        path: location.pathname,
        ...properties
      });
    },
    [location.pathname]
  );

  const trackNavigation = useCallback((from: string, to: string, properties?: Record<string, any>) => {
    telemetryService.trackNavigation(from, to, properties);
  }, []);

  const trackFeature = useCallback(
    (featureName: string, properties?: Record<string, any>) => {
      telemetryService.trackFeatureUsage(featureName, {
        path: location.pathname,
        ...properties
      });
    },
    [location.pathname]
  );

  const trackFileUpload = useCallback(
    (fileName: string, fileSize: number, fileType: string, properties?: Record<string, any>) => {
      telemetryService.trackEvent('FileUpload', {
        fileName,
        fileSizeKB: Math.round(fileSize / 1024),
        fileType,
        path: location.pathname,
        ...properties
      });
    },
    [location.pathname]
  );

  const trackSearch = useCallback(
    (searchTerm: string, resultCount: number, properties?: Record<string, any>) => {
      telemetryService.trackEvent('Search', {
        searchTermHash: telemetryService.hashSensitiveData(searchTerm),
        resultCount,
        path: location.pathname,
        ...properties
      });
    },
    [location.pathname]
  );

  return {
    trackClick,
    trackFormSubmit,
    trackFormAbandoned,
    trackFormValidationFailed,
    trackCustomAction,
    trackNavigation,
    trackFeature,
    trackFileUpload,
    trackSearch
  };
}

export function usePageViewTracking(pageName?: string, properties?: Record<string, any>) {
  const location = useLocation();
  const previousPath = useRef<string>('');

  useEffect(() => {
    telemetryService.trackPageView(pageName || document.title, location.pathname + location.search, {
      previousPath: previousPath.current,
      ...properties
    });

    // Log navigation between routes
    if (previousPath.current && previousPath.current !== location.pathname) {
      telemetryService.trackNavigation(previousPath.current, location.pathname, properties);
    }

    previousPath.current = location.pathname;
  }, [location.pathname, location.search, pageName, properties]);
}

export function useErrorTracking(componentName: string) {
  const location = useLocation();

  return useCallback(
    (error: Error, properties?: Record<string, any>) => {
      telemetryService.trackException(error, undefined, {
        component: componentName,
        path: location.pathname,
        ...properties
      });
    },
    [componentName, location.pathname]
  );
}

export function usePerformanceTracking() {
  const measurements = useRef<Map<string, number>>(new Map());

  const startMeasure = useCallback((measurementName: string) => {
    measurements.current.set(measurementName, Date.now());
    telemetryService.startTrackEvent(measurementName);
  }, []);

  const endMeasure = useCallback((measurementName: string, properties?: Record<string, any>) => {
    const startTime = measurements.current.get(measurementName);
    if (startTime) {
      const duration = Date.now() - startTime;
      measurements.current.delete(measurementName);

      telemetryService.stopTrackEvent(measurementName, properties, { duration });
      telemetryService.trackMetric(measurementName, duration, properties);
    }
  }, []);

  return { startMeasure, endMeasure };
}
